import { ProductLineItem } from '@time-line/components/common';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { BundleAccordionItem } from 'components/product/BundleAccordionItem';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { DEFAULT_TITLE } from 'lib/constants/products';
import type { SanityProduct } from 'types/cms/SanityProduct';
import type { Variant } from 'types/product/Variant';

const getTitleConfig = (
  product: SanityProduct,
  isLoading: boolean,
  variantCount: number,
  variant?: Variant,
) => {
  if (isLoading) return { title: '', subtitle: undefined };

  if (!variantCount) return { title: product.title, subtitle: undefined };

  if (variantCount === 1) {
    if (product.altTitle?.title) return product.altTitle;
    return { title: product.title, subtitle: undefined };
  }

  return {
    title: product.title,
    subtitle:
      variant && variant.title !== DEFAULT_TITLE ? variant.title : undefined,
  };
};

interface BundleLineItemProps {
  product: SanityProduct;
  isFree: boolean;
  variantGid?: string;
  isLast?: boolean;
}

export function BundleLineItem({
  product,
  isFree,
  variantGid,
  isLast = false,
}: BundleLineItemProps) {
  const {
    value: { countryCode, currencyCode },
  } = useLocalStorage(UserLocale);
  const { data: shopifyProduct, isLoading } = useShopifyProduct(product.slug);
  const variants = shopifyProduct?.variants ?? [];
  const variant = variants.find(({ id }) => id === variantGid) ?? variants[0];

  const { title, subtitle: subTitle } = getTitleConfig(
    product,
    isLoading,
    variants.length,
    variant,
  );

  return (
    <BundleAccordionItem
      product={product}
      isLast={isLast}
      header={
        <>
          <ProductLineItem
            flex={1}
            py={0}
            showPrice={false}
            colorScheme="gray"
            title={title}
            subTitle={subTitle}
            image={variant?.imageUrl}
            price={variant?.price?.amount ?? 0}
            countryCode={countryCode}
            currencyCode={currencyCode}
            availableForSale={
              variant?.availableForSale ??
              shopifyProduct?.availableForSale ??
              true
            }
          />
          {isFree && (
            <FormatPrice
              amount={0}
              showFreeIfZero
              color="red.300"
              size="title2"
              fontWeight="medium"
              textTransform="uppercase"
              mr="4xs"
            />
          )}
        </>
      }
    />
  );
}
